<template>
    <DropdownMenu :modal="false">
        <DropdownMenuTrigger as-child>
            <button
                type="button"
                class="flex items-center gap-2 py-1 text-sm transition-colors"
                :class="
                    block
                        ? 'w-full justify-between rounded-md border border-border px-3 py-2.5 hover:bg-muted'
                        : 'min-w-[120px] justify-between text-foreground hover:text-foreground/80'
                "
            >
                <span class="flex min-w-0 items-center gap-2">
                    <span
                        class="flex size-6 shrink-0 items-center justify-center overflow-hidden bg-muted text-[10px] font-medium text-new-blue-500"
                    >
                        <img
                            v-if="current?.profile_photo_path || current?.profile_photo_url"
                            :src="current?.profile_photo_path || current?.profile_photo_url"
                            class="size-full object-cover"
                            alt=""
                        />
                        <template v-else>{{ teamInitial }}</template>
                    </span>
                    <span class="truncate text-foreground">{{ current?.name }}</span>
                </span>
                <ChevronsUpDown class="size-4 shrink-0 text-muted-foreground" />
            </button>
        </DropdownMenuTrigger>

        <DropdownMenuContent
            class="min-w-56"
            :align="block ? 'center' : 'start'"
            :side="block ? 'bottom' : 'bottom'"
            :side-offset="4"
        >
            <DropdownMenuLabel class="text-xs text-muted-foreground">
                Personal account
            </DropdownMenuLabel>

            <DropdownMenuItem class="gap-2 p-2" @click="switchToPersonalTeam">
                <span class="size-8 shrink-0 overflow-hidden bg-white">
                    <img
                        :src="userProps?.profile_photo_path || userProps?.profile_photo_url"
                        class="size-8 object-cover"
                        alt=""
                    />
                </span>
                {{ userProps?.name }}
            </DropdownMenuItem>

            <DropdownMenuSeparator v-if="filteredTeamData.length || canCreateTeam" />

            <DropdownMenuGroup v-if="filteredTeamData.length">
                <DropdownMenuLabel class="text-xs text-muted-foreground">
                    Teams
                </DropdownMenuLabel>
                <DropdownMenuItem
                    v-for="(team, index) in filteredTeamData"
                    :key="index"
                    class="gap-2 p-2"
                    @click="switchTeam(team.id)"
                >
                    <span class="size-8 shrink-0 overflow-hidden bg-white">
                        <img
                            :src="team.profile_photo_path || team.profile_photo_url"
                            class="size-8 object-cover"
                            alt=""
                        />
                    </span>
                    {{ team.name }}
                    <Badge v-if="team.user_id === userProps?.id">owner</Badge>
                </DropdownMenuItem>
            </DropdownMenuGroup>

            <DropdownMenuSeparator v-if="filteredTeamData.length && canCreateTeam" />

            <DropdownMenuItem
                v-if="canCreateTeam"
                class="gap-2 p-2"
                @select.prevent
                @click="openDialog = true"
            >
                <span class="flex size-6 items-center justify-center border bg-background">
                    <Plus class="size-4" />
                </span>
                <span class="font-medium text-muted-foreground">Create team</span>
            </DropdownMenuItem>

            <CreateTeamModalV2
                v-if="canCreateTeam"
                :open="openDialog"
                @update:open="openDialog = $event"
                @team-created="fetchTeams"
            />
        </DropdownMenuContent>
    </DropdownMenu>
</template>

<script lang="ts" setup>
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuGroup,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Badge } from '@/components/ui/badge';
import { ChevronsUpDown, Plus } from 'lucide-vue-next';
import ICurrentTeam from '@/types/Members/ICurrentTeam';
import IUser from '@/types/Members/IUser';
import { ITopolPageProps } from '@/types/TopolPageProps/TopolPageProps';

withDefaults(defineProps<{ block?: boolean }>(), { block: false });

const globalProps = computed(() => usePage<ITopolPageProps>().props);
const userProps = computed(
    (): IUser | null => (globalProps.value.user as IUser | undefined) ?? null
);
const teamProps = computed(
    (): ICurrentTeam | null =>
        (globalProps.value.team as ICurrentTeam | undefined) ?? null
);

const openDialog = ref(false);

// Teams are a PRO feature; the plugin app has no use for them.
const canCreateTeam = computed(() => userProps.value?.app_type !== 'plugin');

const { fetchTeams, getCurrentTeamMembersCount, allTeams } = useTeamsAndMembers();
const { fetchBanners } = useBanners();
const { fetchSubscriptions } = useSubscriptions();
const { resetCurrentFolderWithoutFetching } = useFolders();

const current = computed(() => {
    if (
        !teamProps.value ||
        (teamProps.value.personal_team && teamProps.value.owner_id === userProps.value?.id)
    ) {
        return userProps.value;
    }
    return teamProps.value;
});

const teamInitial = computed(() => current.value?.name?.charAt(0).toUpperCase() ?? '');

const switchTeam = (teamId: number) => {
    if (teamProps.value?.id === teamId) return;
    resetCurrentFolderWithoutFetching();
    router.post(
        route('teams.switch.store'),
        { id: teamId },
        {
            onSuccess: () => {
                fetchSubscriptions();
                fetchBanners();
                getCurrentTeamMembersCount();
            },
        }
    );
};

const switchToPersonalTeam = () => {
    const personalTeam = allTeams.value.find((team) => team.personal_team === true);
    if (!personalTeam) return;
    switchTeam(personalTeam.id);
};

const filteredTeamData = computed(() => {
    return allTeams.value.filter(
        (team) => !(team.personal_team && team.owner_id === userProps.value?.id)
    );
});
</script>
